import { Box, IconButton, Spinner, Text, VStack } from "@chakra-ui/react-v3";
import { useCallback } from "react";
import { RiAddLine, RiCheckLine } from "react-icons/ri";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

type OnItemClickArgs = {
  dest?: unknown;
  pageIndex: number;
  pageNumber: number;
};

type PdfDocumentViewerProps = {
  pdfUrl: string | undefined;
  error: string | undefined;
  isLoading: boolean;
  pdfContainerRef: React.RefObject<HTMLDivElement>;
  isPanning: boolean;
  numPages: number | null;
  scale: number;
  onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
  onDocumentLoadError: (error: Error) => void;
  selectedPages: Set<number>;
  onTogglePageSelection: (pageNum: number) => void;
  disablePageSelection?: boolean;
};

export function PdfDocumentViewer({
  pdfUrl,
  error,
  isLoading,
  pdfContainerRef,
  isPanning,
  numPages,
  scale,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  selectedPages,
  onTogglePageSelection,
  disablePageSelection = false,
}: PdfDocumentViewerProps) {
  const handleItemClick = useCallback(
    ({ pageNumber, dest }: OnItemClickArgs) => {
      const container = pdfContainerRef.current;
      if (!container) return;

      const targetPage = container.querySelector<HTMLElement>(
        `.react-pdf__Page[data-page-number="${pageNumber}"]`
      );
      if (!targetPage) return;

      const destArr = Array.isArray(dest) ? dest : undefined;
      const isXYZ = destArr && destArr.length >= 4 && destArr[1]?.name === "XYZ";
      const yPdf = isXYZ ? (destArr[3] as number | null) : null;

      if (typeof yPdf === "number") {
        const renderedHeight = targetPage.clientHeight;
        const containerRect = container.getBoundingClientRect();
        const pageRect = targetPage.getBoundingClientRect();
        const pageTopInScroll = container.scrollTop + (pageRect.top - containerRect.top);

        // PDF Y is from bottom-left origin; DOM is from top-left.
        // renderedHeight = pdfPageHeight * scale, so:
        // domOffsetFromPageTop = renderedHeight - (yPdf * scale)
        const yOffset = renderedHeight - yPdf * scale;
        container.scrollTo({
          top: pageTopInScroll + Math.max(0, yOffset),
          behavior: "smooth",
        });
        return;
      }

      targetPage.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [pdfContainerRef, scale]
  );

  return (
    <Box
      ref={pdfContainerRef}
      flex="1"
      overflowY="auto"
      overflowX="auto"
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      p={4}
      bg="bg.muted"
      minH="700px"
      cursor={isPanning ? "grab" : "default"}
      _active={isPanning ? { cursor: "grabbing" } : undefined}
    >
      {isLoading && (
        <VStack gap={4} py={12}>
          <Spinner size="xl" color="status.info" />
          <Text fontSize="sm" color="fg.muted">
            Loading PDF... This may take up to 30 seconds.
          </Text>
        </VStack>
      )}

      {error && !isLoading && (
        <VStack gap={2} py={12}>
          <Text fontSize="md" color="status.error" fontWeight="medium">
            Failed to load PDF
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {error}
          </Text>
        </VStack>
      )}

      {pdfUrl && !error && (
        <Document
          file={pdfUrl}
          onItemClick={handleItemClick}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <VStack gap={4} py={12}>
              <Spinner size="xl" color="status.info" />
              <Text fontSize="sm" color="fg.muted">
                Rendering PDF...
              </Text>
            </VStack>
          }
          error={
            <VStack gap={2} py={12}>
              <Text fontSize="md" color="status.error" fontWeight="medium">
                Failed to render PDF
              </Text>
              <Text fontSize="sm" color="fg.muted">
                The PDF file may be corrupted or incompatible.
              </Text>
            </VStack>
          }
        >
          <VStack gap={4} align="center">
            {numPages &&
              Array.from({ length: numPages }, (_, index) => {
                const pageNum = index + 1;
                const isSelected = selectedPages.has(pageNum);

                return (
                  <Box
                    key={`page_${pageNum}`}
                    position="relative"
                    boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
                    bg="white"
                    css={{
                      /* Ensure page wrapper doesn't interfere with rendering */
                      contain: "layout style paint",
                      contentVisibility: "auto",
                    }}
                    outline={isSelected ? "3px solid" : "none"}
                    outlineColor={isSelected ? "btn.success.hoverBorder" : "transparent"}
                    _hover={{
                      "& > .page-select-overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Page
                      pageNumber={pageNum}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      scale={scale}
                      loading={
                        <Box py={12} px={20}>
                          <Spinner size="lg" color="status.info" />
                        </Box>
                      }
                    />
                    {!disablePageSelection && (
                      <Box
                        className="page-select-overlay"
                        position="absolute"
                        top={2}
                        right={2}
                        opacity={isSelected ? 1 : 0}
                        transition="opacity 0.2s"
                        zIndex={10}
                      >
                        <IconButton
                          aria-label={isSelected ? "Remove page from export" : "Add page to export"}
                          size="sm"
                          colorPalette={isSelected ? "green" : "blue"}
                          variant={isSelected ? "solid" : "outline"}
                          onClick={e => {
                            e.stopPropagation();
                            onTogglePageSelection(pageNum);
                          }}
                          bg={isSelected ? "green.500" : "white"}
                          _hover={{
                            bg: isSelected ? "green.600" : "gray.100",
                          }}
                        >
                          {isSelected ? <RiCheckLine /> : <RiAddLine />}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })}
          </VStack>
        </Document>
      )}
    </Box>
  );
}
